import { useAuthStore } from '@/store/auth.store';
import type { AdminUser } from '@/services/admin.service';
import type { TokenPair, User } from '@/types/auth.types';

const AUTH_KEY = 'auth-storage';
const BACKUP_KEY = 'impersonation-admin-backup';
const INFO_KEY = 'impersonation-info';

/**
 * Démarre une session "se connecter en tant que" : sauvegarde la session admin,
 * bascule l'auth store sur le token du user cible, puis recharge l'app.
 */
export function startImpersonation(
  target: AdminUser,
  accessToken: string,
  expiresIn: number,
): void {
  const current = localStorage.getItem(AUTH_KEY);
  if (current) localStorage.setItem(BACKUP_KEY, current);
  localStorage.setItem(
    INFO_KEY,
    JSON.stringify({
      email: target.email,
      name:
        `${target.firstName ?? ''} ${target.lastName ?? ''}`.trim() ||
        target.email,
    }),
  );

  const impersonatedUser = {
    id: target.uuid,
    uuid: target.uuid,
    email: target.email,
    role: target.role,
    firstName: target.firstName ?? '',
    lastName: target.lastName ?? '',
    preferredLanguage: 'fr',
    emailNotifications: true,
    pushNotifications: true,
    lastLoginAt: null,
    isActive: true,
    createdAt: '',
    updatedAt: '',
    subscription: undefined,
    twoFactorEnabled: false,
    isTwoFactorEnabled: false,
    securityMethod: null,
  } as User;

  const tokens: TokenPair = {
    accessToken,
    refreshToken: '',
    expiresIn,
    tokenType: 'Bearer',
  };

  useAuthStore.getState().setTokens(tokens);
  useAuthStore.getState().setUser(impersonatedUser);

  window.location.href = '/';
}

export function isImpersonating(): boolean {
  return !!localStorage.getItem(BACKUP_KEY);
}

export function getImpersonationInfo(): { email: string; name: string } | null {
  const raw = localStorage.getItem(INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Restaure la session admin d'origine et recharge. */
export function stopImpersonation(): void {
  const backup = localStorage.getItem(BACKUP_KEY);
  if (backup) localStorage.setItem(AUTH_KEY, backup);
  localStorage.removeItem(BACKUP_KEY);
  localStorage.removeItem(INFO_KEY);
  window.location.href = '/admin';
}
